import React, { useState, useEffect } from "react";
import axios from "../utils/axios";
import Config from "../utils/config";
import Spinner from "../components/Spinner";

const Statistic = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${Config.baseURL}/statistics`);
        setStats(response.data);
      } catch (err) {
        console.error("Error fetching statistics:", err);
        setError("Impossible de charger les statistiques.");
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) return <Spinner heading="Chargement des statistiques..." />;
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="bg-white rounded-lg border border-gray-200 p-6 text-center text-gray-700">
          {error}
        </div>
      </div>
    );
  }
  if (!stats) return null;

  const { recruitments, agents, availability, workingHours } = stats;
  const currentMonthLabel = availability?.currentMonth
    ? new Date(availability.currentMonth + "-01").toLocaleDateString("fr-FR", {
        month: "long",
        year: "numeric",
      })
    : "";

  const cards = [
    { title: "Total recrutements", value: recruitments?.total ?? 0, sub: "Candidatures reçues" },
    { title: "En cours", value: recruitments?.pending ?? 0, sub: "En attente de traitement" },
    { title: "Approuvées", value: recruitments?.approved ?? 0, sub: "Candidatures acceptées" },
    { title: "Rejetées", value: recruitments?.rejected ?? 0, sub: "Candidatures refusées" },
    { title: "Agents", value: agents?.total ?? 0, sub: "Agents actifs (approuvés)" },
    { title: "Disponibilités ce mois", value: availability?.filledThisMonth ?? 0, sub: currentMonthLabel },
    { title: "Relevés d'heures ce mois", value: workingHours?.submittedThisMonth ?? 0, sub: currentMonthLabel },
  ];

  const total = recruitments?.total ?? 0;
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 overflow-x-hidden w-full min-w-0">
      <div className="max-w-5xl mx-auto space-y-6 sm:space-y-8 w-full">
        <h1 className="text-lg sm:text-xl font-semibold text-gray-800">Statistiques</h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {cards.map((card) => (
            <div
              key={card.title}
              className="bg-white rounded-lg border border-gray-200 p-5 hover:border-gray-300 transition-colors"
            >
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">{card.title}</p>
              <p className="text-3xl font-semibold text-gray-900 mt-1">{card.value}</p>
              {card.sub && <p className="text-sm text-gray-500 mt-1">{card.sub}</p>}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-base font-semibold text-gray-800 mb-5">Répartition des recrutements</h2>
          <div className="space-y-4">
            {[
              { label: "En cours", count: recruitments?.pending ?? 0, bar: "bg-gray-400" },
              { label: "Approuvées", count: recruitments?.approved ?? 0, bar: "bg-gray-700" },
              { label: "Rejetées", count: recruitments?.rejected ?? 0, bar: "bg-gray-500" },
            ].map(({ label, count, bar }) => (
              <div key={label}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-600">{label}</span>
                  <span className="font-medium text-gray-900 tabular-nums">{count}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div
                    className={`${bar} h-2 rounded-full transition-all duration-300`}
                    style={{ width: `${pct(count)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Statistic;
